import type {
  Category,
  CatalogProvider,
  CartProvider,
  IdentityProvider,
  ModelSummary,
  PricingProvider,
  ProductModel,
  QuoteLineInput,
  QuoteResult,
} from '@ox/schema';

const PEN = { amount: 55, currency: 'PEN' } as const;
const PEN_XXL = { amount: 65, currency: 'PEN' } as const;

const sizes = [
  { id: 's', label: 'S', order: 1 },
  { id: 'm', label: 'M', order: 2 },
  { id: 'l', label: 'L', order: 3 },
  { id: 'xl', label: 'XL', order: 4 },
  { id: 'xxl', label: 'XXL', order: 5 },
];

const colors = [
  { key: 'negro', label: 'Negro', hex: '#1a1a1a', isDark: true },
  { key: 'blanco', label: 'Blanco', hex: '#f5f5f5', isDark: false },
  { key: 'rojo', label: 'Rojo', hex: '#c62828', isDark: true },
  { key: 'azul', label: 'Azul', hex: '#1565c0', isDark: true },
  { key: 'beige', label: 'Beige', hex: '#d7c4a3', isDark: false },
];

function mockupUrl(apiBase: string, templateId: string, view: string, hex: string, w = 537) {
  // Same endpoint as Woo — template silhouette+shading (matches admin calibration).
  return `${apiBase}/templates/${encodeURIComponent(templateId)}/mockup/${view}?color=${hex.replace('#', '')}&w=${w}`;
}

function buildModel(apiBase: string, id: string, name: string, categoryId: string): ProductModel {
  return {
    id,
    name,
    category: { id: categoryId, label: categoryId },
    rating: { value: 4.5, count: 140 },
    templateId: id,
    views: [
      { id: 'front', label: 'Delante' },
      { id: 'back', label: 'Detrás' },
    ],
    printAreas: {
      // Approximate chest box on the FPD women-tshirt / hoodie mockups (mm on garment).
      // Exact values come from admin calibration later.
      front: { x: 70, y: 95, width: 160, height: 200, unit: 'mm' },
      back: { x: 70, y: 95, width: 160, height: 200, unit: 'mm' },
    },
    printMethod: 'dtg',
    minDpi: 150,
    colors: colors.map((c) => ({
      ...c,
      available: true,
      mockups: {
        front: { url: mockupUrl(apiBase, id, 'front', c.hex), pxPerMm: 1.79 },
        back: { url: mockupUrl(apiBase, id, id === 'polo-clasico-unisex' ? 'front' : 'back', c.hex), pxPerMm: 1.79 },
      },
    })),
    sizes,
    sizeRangeLabel: 'S - XXL',
    // Demo: leave unset so the link stays hidden unless configured.
    variants: colors.flatMap((c) =>
      sizes.map((s) => ({
        id: `mem:${id}:${c.key}:${s.id}`,
        colorKey: c.key,
        sizeId: s.id,
        availability: s.id === 'xxl' && c.key === 'lila' ? ('out_of_stock' as const) : ('in_stock' as const),
        availabilityLabel: s.id === 'xxl' && c.key === 'lila' ? 'Agotado temporalmente' : undefined,
        price: s.id === 'xxl' ? PEN_XXL : PEN,
      })),
    ),
    priceFrom: PEN,
    priceTo: PEN_XXL,
    promoNote: {
      text: 'Consigue descuentos por cantidad',
      modalTitle: 'Descuentos por volumen',
      modalHtml:
        '<p><strong>-15%</strong> desde 3 artículos</p>' +
        '<p><strong>-20%</strong> desde 5 artículos</p>' +
        '<p><strong>-30%</strong> desde 10 artículos</p>' +
        '<p><em>El descuento se aplica al calcular el total en la cesta.</em></p>',
    },
    deliveryHtml:
      '<div><strong>Estándar:</strong> 1-2 días Lima</div>' +
      '<div><strong>Express:</strong> 24h Lima</div>',
  };
}

export function createMemoryAdapter(apiBase = 'http://localhost:4010'): {
  catalog: CatalogProvider;
  pricing: PricingProvider;
  cart: CartProvider;
  identity: IdentityProvider;
} {
  const models: ProductModel[] = [
    buildModel(apiBase, 'polo-clasico-unisex', 'Camiseta mujer (demo)', 'mujer'),
    buildModel(apiBase, 'hoodie-hombre', 'Polera hombre (demo)', 'sudaderas'),
  ];

  const categories: Category[] = [
    { id: 'populares', label: 'Populares', children: [] },
    {
      id: 'hombre',
      label: 'Ropa de Hombre',
      children: [{ id: 'sudaderas', label: 'Sudaderas', children: [] }],
    },
    { id: 'mujer', label: 'Mujer', children: [] },
  ];

  let cartCount = 0;

  return {
    catalog: {
      async listCategories() {
        return categories;
      },
      async listModels(filter) {
        const cat = filter?.categoryId;
        return models
          .filter((m) => {
            if (!cat || cat === 'populares') return true;
            if (m.category.id === cat) return true;
            // Demo parent «Ropa de Hombre» includes Sudaderas leaf.
            if (cat === 'hombre' && m.category.id === 'sudaderas') return true;
            return false;
          })
          .map(
            (m): ModelSummary => ({
              id: m.id,
              name: m.name,
              thumbnail: m.colors[0]!.mockups.front!.url,
              colorCount: m.colors.length,
              priceFrom: m.priceFrom,
              categoryId: m.category.id,
            }),
          );
      },
      async getModel(id) {
        const m = models.find((x) => x.id === id);
        if (!m) throw new Error(`Unknown model ${id}`);
        return m;
      },
      async resolveEntry(ref) {
        // ref like "product:123" → default model
        return { modelId: models[0]!.id, colorKey: 'negro' };
      },
    },
    pricing: {
      async quote(input): Promise<QuoteResult> {
        const lines = input.lines.map((l: QuoteLineInput) => {
          const parts = l.variantId.split(':');
          const sizeId = parts[3];
          const unit = sizeId === 'xxl' ? PEN_XXL : PEN;
          return {
            variantId: l.variantId,
            quantity: l.quantity,
            unitPrice: unit,
            lineTotal: { amount: unit.amount * l.quantity, currency: 'PEN' },
          };
        });
        const qty = lines.reduce((s, l) => s + l.quantity, 0);
        const subtotalAmount = lines.reduce((s, l) => s + l.lineTotal.amount, 0);
        const discounts =
          qty >= 3
            ? [{ label: '-15% a partir de 3 artículos', amount: { amount: subtotalAmount * 0.15, currency: 'PEN' } }]
            : [];
        const discountTotal = discounts.reduce((s, d) => s + d.amount.amount, 0);
        return {
          lines,
          subtotal: { amount: subtotalAmount, currency: 'PEN' },
          discounts,
          total: { amount: Math.max(0, subtotalAmount - discountTotal), currency: 'PEN' },
          note: qty >= 3 ? undefined : '-15% a partir de 3 artículos',
        };
      },
    },
    cart: {
      async addToCart(input) {
        cartCount += input.lines.reduce((s, l) => s + l.quantity, 0);
        return { cartCount, redirectUrl: undefined };
      },
      async getCart() {
        return { cartCount, redirectUrl: undefined };
      },
    },
    identity: {
      async getToken() {
        return null;
      },
      async getOwnerRef() {
        return 'guest:memory';
      },
    },
  };
}
